import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { 
  Loader2, 
  RefreshCcw, 
  Plus, 
  Edit3, 
  Check, 
  X, 
  DollarSign,
  Percent,
  Package,
  Infinity as InfinityIcon,
  Palette,
  Star
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useSubscriptionPlans, SubscriptionPlan, UpdatePlanData, CreatePlanData } from "@/hooks/useSubscriptionPlans";
import { useAuth } from "@/contexts/AuthContext";
import { PLAN_COLORS } from "@/lib/constants";
import { getLocalizedValue } from "@/i18n/utils";

type TabType = "plans" | "edit" | "create";

export default function AdminSubscriptions() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const { loading, error, fetchPlans, updatePlan, createPlan } = useSubscriptionPlans();
  
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>("plans");
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // 新方案表單狀態
  const [newPlan, setNewPlan] = useState<CreatePlanData>({
    name: "",
    display_name: { "zh-TW": "", en: "" },
    description: { "zh-TW": "", en: "" },
    monthly_price: undefined,
    yearly_price: undefined,
    yearly_discount_percent: 20,
    lifetime_price: undefined,
    features: { "zh-TW": [], en: [] },
    limits: {
      maxScripts: -1,
      maxCharactersPerScript: -1,
      hasMirrorMode: true,
      hasCustomFonts: true,
      hasExport: true,
      hasNoWatermark: true,
      hasVoiceSync: false,
      hasOfflineMode: false,
      hasPrioritySupport: false,
    },
    custom_toggles: [],
    color_class: "bg-accent-yellow",
    is_popular: false,
    cta_text: { "zh-TW": "", en: "" },
    sort_order: 99,
  });

  const loadPlans = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const data = await fetchPlans();
      setPlans(data);
    } catch {
      toast.error(t("admin.subscriptions.loadError"));
    }
  }, [session?.access_token, fetchPlans, t]);

  useEffect(() => {
    if (session?.access_token) {
      loadPlans();
    }
  }, [session?.access_token, loadPlans]);

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setActiveTab("edit");
  };

  const handleSavePlan = async () => {
    if (!selectedPlan) return;
    
    setIsSaving(true);
    try {
      const updateData: UpdatePlanData = {
        id: selectedPlan.id,
        display_name: selectedPlan.display_name,
        description: selectedPlan.description,
        monthly_price: selectedPlan.monthly_price ?? undefined,
        yearly_price: selectedPlan.yearly_price ?? undefined,
        yearly_discount_percent: selectedPlan.yearly_discount_percent,
        features: selectedPlan.features,
        limits: selectedPlan.limits,
        custom_toggles: selectedPlan.custom_toggles,
        color_class: selectedPlan.color_class,
        is_popular: selectedPlan.is_popular,
        cta_text: selectedPlan.cta_text,
      };
      
      await updatePlan(updateData);
      toast.success(t("admin.subscriptions.updateSuccess"));
      await loadPlans();
      setActiveTab("plans");
      setSelectedPlan(null);
    } catch {
      toast.error(t("admin.subscriptions.updateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.name || !newPlan.display_name.en) {
      toast.error(t("admin.subscriptions.nameRequired"));
      return;
    }

    setIsSaving(true);
    try {
      await createPlan(newPlan);
      toast.success(t("admin.subscriptions.createSuccess"));
      await loadPlans();
      setActiveTab("plans");
      setNewPlan({
        name: "",
        display_name: { "zh-TW": "", en: "" },
        description: { "zh-TW": "", en: "" },
        monthly_price: undefined,
        yearly_price: undefined,
        yearly_discount_percent: 20,
        lifetime_price: undefined,
        features: { "zh-TW": [], en: [] },
        limits: {
          maxScripts: -1,
          maxCharactersPerScript: -1,
          hasMirrorMode: true,
          hasCustomFonts: true,
          hasExport: true,
          hasNoWatermark: true,
          hasVoiceSync: false,
          hasOfflineMode: false,
          hasPrioritySupport: false,
        },
        sort_order: 99,
      });
    } catch {
      toast.error(t("admin.subscriptions.createError"));
    } finally {
      setIsSaving(false);
    }
  };

  const planColors: Record<string, string> = {
    basic: "#FFD93D",
    pro: "#FF6B9D",
    lifetime: "#6FEDD6",
  };

  const getPlanColor = (name: string) => planColors[name] || "#4DA6FF";

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">
            {t("admin.subscriptions.title")}
          </h2>
          <p className="text-lg font-medium text-gray-500">
            {t("admin.subscriptions.subtitle")}
          </p>
        </div>
        <button 
          onClick={loadPlans}
          disabled={loading}
          className="rounded-xl border-2 border-black bg-white p-3 shadow-[4px_4px_0_#000] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
        >
          <RefreshCcw className={cn("h-6 w-6", loading && "animate-spin")} />
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-8 flex gap-2">
        {[
          { key: "plans", label: t("admin.subscriptions.tabs.plans") },
          { key: "edit", label: t("admin.subscriptions.tabs.edit"), disabled: !selectedPlan },
          { key: "create", label: t("admin.subscriptions.tabs.create") },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => !tab.disabled && setActiveTab(tab.key as TabType)}
            disabled={tab.disabled}
            className={cn(
              "px-4 py-2 font-bold border-2 border-black rounded-lg transition-all",
              activeTab === tab.key
                ? "bg-[#FFD93D] shadow-[4px_4px_0_#000]"
                : tab.disabled
                ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
                : "bg-white hover:bg-gray-50"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border-2 border-red-500 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {/* Plans List Tab */}
      {activeTab === "plans" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && plans.length === 0 ? (
            <div className="col-span-full flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            plans.map((plan) => (
              <div
                key={plan.id}
                className="border-2 border-black rounded-xl overflow-hidden shadow-[4px_4px_0_#000]"
                style={{ backgroundColor: getPlanColor(plan.name) }}
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-black">
                        {getLocalizedValue(plan.display_name, plan.name)}
                      </h3>
                      <p className="text-sm text-black/70">
                        {plan.name}
                      </p>
                    </div>
                    <button
                      onClick={() => handleEditPlan(plan)}
                      className="p-2 bg-white/50 rounded-lg border-2 border-black hover:bg-white transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Pricing */}
                  <div className="space-y-2 mb-4">
                    {plan.name === 'free' ? (
                      <>
                        {/* Free plan placeholder for alignment */}
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">$0</span>
                          <span className="text-sm text-black/70">{t("admin.subscriptions.labels.freeTrial") || "14天免費試用"}</span>
                        </div>
                        {/* Empty space for alignment with paid plans */}
                        <div className="h-6"></div>
                      </>
                    ) : (
                      <>
                        {plan.monthly_price && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-bold">${plan.monthly_price}</span>
                            <span className="text-sm text-black/70">{t("admin.subscriptions.status.month")}</span>
                          </div>
                        )}
                        {plan.yearly_price && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-bold">${plan.yearly_price}</span>
                            <span className="text-sm text-black/70">{t("admin.subscriptions.status.year")}</span>
                            <span className="text-xs bg-black text-white px-2 py-0.5 rounded-full">
                              -{plan.yearly_discount_percent}%
                            </span>
                          </div>
                        )}
                        {plan.lifetime_price && (
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-bold">${plan.lifetime_price}</span>
                            <span className="text-sm text-black/70">{t("admin.subscriptions.status.lifetime")}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Limits */}
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <span>
                        {plan.limits.maxScripts === -1 ? (
                          <InfinityIcon className="w-4 h-4 inline" />
                        ) : (
                          plan.limits.maxScripts
                        )}{" "}
                        {t("admin.subscriptions.status.scripts")}
                      </span>
                    </div>
                  </div>

                  {/* Sync Status */}
                  <div className="mt-4 pt-4 border-t border-black/20 flex items-center gap-2 text-xs">
                    {plan.name === 'free' ? (
                      <>
                        {/* Supabase Logo SVG */}
                        <svg className="w-4 h-4" viewBox="0 0 109 113" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" fill="url(#paint0_linear)"/>
                          <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" fill="url(#paint1_linear)" fillOpacity="0.2"/>
                          <path d="M45.317 2.07103C48.1765 -1.53037 53.9745 0.442937 54.0434 5.041L54.4849 72.2922H9.83113C1.64038 72.2922 -2.92775 62.8321 2.1655 56.4175L45.317 2.07103Z" fill="#3ECF8E"/>
                          <defs>
                            <linearGradient id="paint0_linear" x1="53.9738" y1="54.974" x2="94.1635" y2="71.8295" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#249361"/>
                              <stop offset="1" stopColor="#3ECF8E"/>
                            </linearGradient>
                            <linearGradient id="paint1_linear" x1="36.1558" y1="30.578" x2="54.4844" y2="65.0806" gradientUnits="userSpaceOnUse">
                              <stop/>
                              <stop offset="1" stopOpacity="0"/>
                            </linearGradient>
                          </defs>
                        </svg>
                        <span>{t("admin.subscriptions.status.supabaseSynced") || "Supabase 已同步"}</span>
                      </>
                    ) : plan.stripe_product?.active ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>{t("admin.subscriptions.status.synced")}</span>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4 text-red-700" />
                        <span>{t("admin.subscriptions.status.notSynced")}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Add new plan card */}
          <button
            onClick={() => setActiveTab("create")}
            className="border-2 border-dashed border-gray-400 rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover:border-black hover:bg-gray-50 transition-all min-h-[200px]"
          >
            <Plus className="w-8 h-8 text-gray-400" />
            <span className="font-bold text-gray-500">
              {t("admin.subscriptions.addPlan")}
            </span>
          </button>
        </div>
      )}

      {/* Edit Plan Tab */}
      {activeTab === "edit" && selectedPlan && (
        <div className="border-2 border-black rounded-xl bg-white shadow-[4px_4px_0_#000] p-6">
          <h3 className="text-xl font-black mb-6">
            {t("admin.subscriptions.editing")} {getLocalizedValue(selectedPlan.display_name)}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Display Name */}
            <div>
              <label className="block text-sm font-bold mb-2">
                {t("admin.subscriptions.labels.displayNameZh")}
              </label>
              <input
                type="text"
                value={selectedPlan.display_name["zh-TW"] || ""}
                onChange={(e) =>
                  setSelectedPlan({
                    ...selectedPlan,
                    display_name: { ...selectedPlan.display_name, "zh-TW": e.target.value },
                  })
                }
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">
                {t("admin.subscriptions.labels.displayNameEn")}
              </label>
              <input
                type="text"
                value={selectedPlan.display_name.en || ""}
                onChange={(e) =>
                  setSelectedPlan({
                    ...selectedPlan,
                    display_name: { ...selectedPlan.display_name, en: e.target.value },
                  })
                }
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            {/* Pricing */}
            <div>
              <label className="block text-sm font-bold mb-2 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                {t("admin.subscriptions.labels.monthlyPrice")}
              </label>
              <input
                type="number"
                step="0.01"
                value={selectedPlan.monthly_price || ""}
                onChange={(e) => {
                  const monthlyPrice = e.target.value ? parseFloat(e.target.value) : null;
                  const discountPercent = selectedPlan.yearly_discount_percent ?? 0;
                  const yearlyPrice = monthlyPrice == null
                    ? null
                    : Math.round(monthlyPrice * 12 * (1 - discountPercent / 100) * 100) / 100;

                  setSelectedPlan({
                    ...selectedPlan,
                    monthly_price: monthlyPrice,
                    yearly_price: yearlyPrice,
                  });
                }}
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2 flex items-center gap-2">
                <Percent className="w-4 h-4" />
                {t("admin.subscriptions.labels.yearlyDiscount")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={selectedPlan.yearly_discount_percent ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // 只允許空字串或數字
                  if (value === "" || /^\d+$/.test(value)) {
                    const discount = value === "" ? 0 : Math.min(100, parseInt(value));
                    const monthlyPrice = selectedPlan.monthly_price || 0;
                    const yearlyPrice = monthlyPrice * 12 * (1 - discount / 100);
                    setSelectedPlan({
                      ...selectedPlan,
                      yearly_discount_percent: value === "" ? (value as unknown as number) : discount,
                      yearly_price: Math.round(yearlyPrice * 100) / 100,
                    });
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  const num = parseInt(value);
                  const discount = isNaN(num) ? 0 : Math.min(100, Math.max(0, num));
                  const monthlyPrice = selectedPlan.monthly_price || 0;
                  const yearlyPrice = monthlyPrice * 12 * (1 - discount / 100);
                  setSelectedPlan({
                    ...selectedPlan,
                    yearly_discount_percent: discount,
                    yearly_price: Math.round(yearlyPrice * 100) / 100,
                  });
                }}
                placeholder="0"
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
              {selectedPlan.monthly_price && (
                <p className="text-sm text-gray-500 mt-1">
                  {t("admin.subscriptions.labels.yearlyPrice")}: ${selectedPlan.yearly_price?.toFixed(2)}
                  {selectedPlan.monthly_price * 12 - (selectedPlan.yearly_price || 0) > 0 && 
                    ` (${t("admin.subscriptions.labels.saveAmount")} $${((selectedPlan.monthly_price * 12) - (selectedPlan.yearly_price || 0)).toFixed(2)})`
                  }
                </p>
              )}
            </div>

            {/* Limits */}
            <div>
              <label className="block text-sm font-bold mb-2">
                {t("admin.subscriptions.labels.maxScripts")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={selectedPlan.limits?.maxScripts ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // 允許空字串、負號、或數字
                  if (value === "" || value === "-" || /^-?\d+$/.test(value)) {
                    setSelectedPlan({
                      ...selectedPlan,
                      limits: { 
                        ...selectedPlan.limits, 
                        maxScripts: value === "" || value === "-" ? (value as unknown as number) : parseInt(value)
                      },
                    });
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  // 離開欄位時確保是有效數字
                  const num = parseInt(value);
                  setSelectedPlan({
                    ...selectedPlan,
                    limits: { 
                      ...selectedPlan.limits, 
                      maxScripts: isNaN(num) ? -1 : num
                    },
                  });
                }}
                placeholder="-1"
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">
                {t("admin.subscriptions.labels.maxChars")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={selectedPlan.limits?.maxCharactersPerScript ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // 允許空字串、負號、或數字
                  if (value === "" || value === "-" || /^-?\d+$/.test(value)) {
                    setSelectedPlan({
                      ...selectedPlan,
                      limits: { 
                        ...selectedPlan.limits, 
                        maxCharactersPerScript: value === "" || value === "-" ? (value as unknown as number) : parseInt(value)
                      },
                    });
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  // 離開欄位時確保是有效數字
                  const num = parseInt(value);
                  setSelectedPlan({
                    ...selectedPlan,
                    limits: { 
                      ...selectedPlan.limits, 
                      maxCharactersPerScript: isNaN(num) ? -1 : num
                    },
                  });
                }}
                placeholder="-1"
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            {/* Cloud Storage - Monthly Limit */}
            <div>
              <label className="block text-sm font-bold mb-2">
                {t("admin.subscriptions.labels.cloudStorageMonthly") || "每月上傳限制 (GB)"}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={
                  selectedPlan.limits?.cloudStorageMonthlyBytes
                    ? (Number(selectedPlan.limits.cloudStorageMonthlyBytes) / 1073741824).toFixed(0)
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    const bytes = value === "" ? 0 : parseInt(value) * 1073741824;
                    setSelectedPlan({
                      ...selectedPlan,
                      limits: { 
                        ...selectedPlan.limits, 
                        cloudStorageMonthlyBytes: bytes
                      },
                    });
                  }
                }}
                placeholder="5"
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("admin.subscriptions.labels.cloudStorageMonthlyHint") || "設為 0 表示禁止上傳"}
              </p>
            </div>

            {/* Cloud Storage - Total Limit */}
            <div>
              <label className="block text-sm font-bold mb-2">
                {t("admin.subscriptions.labels.cloudStorageTotal") || "總儲存容量 (GB)"}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={
                  selectedPlan.limits?.cloudStorageTotalBytes
                    ? (Number(selectedPlan.limits.cloudStorageTotalBytes) / 1073741824).toFixed(0)
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    const bytes = value === "" ? 0 : parseInt(value) * 1073741824;
                    setSelectedPlan({
                      ...selectedPlan,
                      limits: { 
                        ...selectedPlan.limits, 
                        cloudStorageTotalBytes: bytes
                      },
                    });
                  }
                }}
                placeholder="20"
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("admin.subscriptions.labels.cloudStorageTotalHint") || "在價格頁面會自動顯示此限制"}
              </p>
            </div>
          </div>

          {/* Feature Toggles */}
          <div className="mt-6 p-4 border-2 border-black rounded-lg bg-white">
            <h4 className="text-sm font-bold mb-3">{t("admin.subscriptions.labels.featureToggles")}</h4>
            
            {/* 預設功能開關 */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">{t("admin.subscriptions.labels.defaultFeatures")}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: "hasMirrorMode", label: t("admin.subscriptions.features.mirrorMode") },
                  { key: "hasCustomFonts", label: t("admin.subscriptions.features.customFonts") },
                  { key: "hasExport", label: t("admin.subscriptions.features.export") },
                  { key: "hasNoWatermark", label: t("admin.subscriptions.features.noWatermark") },
                  { key: "hasVoiceSync", label: t("admin.subscriptions.features.voiceSync") },
                  { key: "hasOfflineMode", label: t("admin.subscriptions.features.offlineMode") },
                  { key: "hasPrioritySupport", label: t("admin.subscriptions.features.prioritySupport") },
                ].map((feature) => (
                  <label key={feature.key} className="flex items-center gap-2 cursor-pointer">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedPlan({
                          ...selectedPlan,
                          limits: {
                            ...selectedPlan.limits,
                            [feature.key]: !selectedPlan.limits[feature.key as keyof typeof selectedPlan.limits],
                          },
                        })
                      }
                      className={cn(
                        "w-5 h-5 border-2 border-black rounded flex items-center justify-center",
                        selectedPlan.limits[feature.key as keyof typeof selectedPlan.limits]
                          ? "bg-[#6FEDD6]"
                          : "bg-white"
                      )}
                    >
                      {selectedPlan.limits[feature.key as keyof typeof selectedPlan.limits] && (
                        <Check className="w-3 h-3" />
                      )}
                    </button>
                    <span className="text-sm">{feature.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 自定義功能開關 */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500 mb-2">{t("admin.subscriptions.labels.customToggles")}</p>
              <div className="space-y-3">
                {(selectedPlan.custom_toggles || []).map((toggle: { key: string; enabled: boolean; labels: Record<string, string> }, index: number) => (
                  <div key={index} className="p-3 border-2 border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newToggles = [...(selectedPlan.custom_toggles || [])];
                          newToggles[index] = { ...toggle, enabled: !toggle.enabled };
                          setSelectedPlan({ ...selectedPlan, custom_toggles: newToggles });
                        }}
                        className={cn(
                          "w-6 h-6 border-2 border-black rounded flex items-center justify-center flex-shrink-0",
                          toggle.enabled ? "bg-[#6FEDD6]" : "bg-white"
                        )}
                      >
                        {toggle.enabled && <Check className="w-4 h-4" />}
                      </button>
                      <span className="text-sm font-medium flex-1">
                        {toggle.labels?.["zh-TW"] || toggle.labels?.en || t("admin.subscriptions.labels.unnamedFeature")}
                      </span>
                      <span className="text-xs text-gray-400 font-mono">
                        {toggle.key}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const newToggles = (selectedPlan.custom_toggles || []).filter((_: unknown, i: number) => i !== index);
                          setSelectedPlan({ ...selectedPlan, custom_toggles: newToggles });
                        }}
                        className="px-2 py-1 border-2 border-red-400 text-red-600 rounded hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("admin.subscriptions.labels.chineseName")}</label>
                        <input
                          type="text"
                          value={toggle.labels?.["zh-TW"] || ""}
                          onChange={(e) => {
                            const newToggles = [...(selectedPlan.custom_toggles || [])];
                            newToggles[index] = { 
                              ...toggle, 
                              labels: { ...toggle.labels, "zh-TW": e.target.value } 
                            };
                            setSelectedPlan({ ...selectedPlan, custom_toggles: newToggles });
                          }}
                          placeholder={t("admin.subscriptions.labels.chineseName")}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B9D]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("admin.subscriptions.labels.englishName")}</label>
                        <input
                          type="text"
                          value={toggle.labels?.en || ""}
                          onChange={(e) => {
                            const englishName = e.target.value;
                            // 從英文名稱自動生成 Key: "Cloud Backup" -> "hasCloudBackup"
                            const generatedKey = englishName
                              ? 'has' + englishName
                                  .split(' ')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                  .join('')
                              : toggle.key;
                            
                            const newToggles = [...(selectedPlan.custom_toggles || [])];
                            newToggles[index] = { 
                              ...toggle, 
                              key: generatedKey,
                              labels: { ...toggle.labels, en: englishName } 
                            };
                            setSelectedPlan({ ...selectedPlan, custom_toggles: newToggles });
                          }}
                          placeholder={t("admin.subscriptions.labels.englishName")}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B9D]"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* 新增自定義開關按鈕 */}
                <button
                  type="button"
                  onClick={() => {
                    const newToggle = {
                      key: `hasCustom${Date.now()}`,
                      enabled: false,
                      labels: { "zh-TW": "", en: "" }
                    };
                    setSelectedPlan({
                      ...selectedPlan,
                      custom_toggles: [...(selectedPlan.custom_toggles || []), newToggle],
                    });
                  }}
                  className="w-full px-3 py-2 border-2 border-dashed border-gray-400 rounded-lg text-gray-500 hover:border-black hover:text-black flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("admin.subscriptions.buttons.addCustomToggle")}
                </button>
              </div>
            </div>
          </div>

          {/* Feature List Editor */}
          <div className="mt-6 p-4 border-2 border-black rounded-lg bg-white">
            <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              {t("admin.subscriptions.labels.featureDescriptions")}
            </h4>
            
            {/* Chinese Features */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">{t("admin.subscriptions.labels.chineseFeatures")}</label>
              <div className="space-y-2">
                {(selectedPlan.features?.["zh-TW"] || []).map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const newFeatures = [...(selectedPlan.features?.["zh-TW"] || [])];
                        newFeatures[index] = e.target.value;
                        setSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, "zh-TW": newFeatures },
                        });
                      }}
                      className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D] focus:border-black"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newFeatures = (selectedPlan.features?.["zh-TW"] || []).filter((_, i) => i !== index);
                        setSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, "zh-TW": newFeatures },
                        });
                      }}
                      className="px-3 py-2 border-2 border-red-400 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newFeatures = [...(selectedPlan.features?.["zh-TW"] || []), ""];
                    setSelectedPlan({
                      ...selectedPlan,
                      features: { ...selectedPlan.features, "zh-TW": newFeatures },
                    });
                  }}
                  className="w-full px-3 py-2 border-2 border-dashed border-gray-400 rounded-lg text-gray-500 hover:border-black hover:text-black flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("admin.subscriptions.buttons.addFeatureZh")}
                </button>
              </div>
            </div>

            {/* English Features */}
            <div>
              <label className="block text-sm font-medium mb-2">{t("admin.subscriptions.labels.englishFeatures")}</label>
              <div className="space-y-2">
                {(selectedPlan.features?.en || []).map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const newFeatures = [...(selectedPlan.features?.en || [])];
                        newFeatures[index] = e.target.value;
                        setSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, en: newFeatures },
                        });
                      }}
                      className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D] focus:border-black"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newFeatures = (selectedPlan.features?.en || []).filter((_, i) => i !== index);
                        setSelectedPlan({
                          ...selectedPlan,
                          features: { ...selectedPlan.features, en: newFeatures },
                        });
                      }}
                      className="px-3 py-2 border-2 border-red-400 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newFeatures = [...(selectedPlan.features?.en || []), ""];
                    setSelectedPlan({
                      ...selectedPlan,
                      features: { ...selectedPlan.features, en: newFeatures },
                    });
                  }}
                  className="w-full px-3 py-2 border-2 border-dashed border-gray-400 rounded-lg text-gray-500 hover:border-black hover:text-black flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("admin.subscriptions.buttons.addFeatureEn")}
                </button>
              </div>
            </div>
          </div>

          {/* Appearance Settings */}
          <div className="mt-6 p-4 border-2 border-black rounded-lg bg-gray-50">
            <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
              <Palette className="w-4 h-4" />
              {t("admin.subscriptions.labels.appearance")}
            </h4>
            
            {/* Color Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">{t("admin.subscriptions.labels.cardColor")}</label>
              <div className="flex flex-wrap gap-2">
                {PLAN_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() =>
                      setSelectedPlan({
                        ...selectedPlan,
                        color_class: color.value,
                      })
                    }
                    className={cn(
                      "w-10 h-10 rounded-lg border-2 flex items-center justify-center transition-all",
                      selectedPlan.color_class === color.value
                        ? "border-black ring-2 ring-offset-2 ring-black"
                        : "border-gray-300 hover:border-gray-500"
                    )}
                    style={{ backgroundColor: color.preview }}
                    title={color.label}
                  >
                    {selectedPlan.color_class === color.value && (
                      <Check className="w-5 h-5 text-white drop-shadow-md" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Toggle */}
            <div className="mb-4">
              <div className="flex items-center gap-3 cursor-pointer">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const current = selectedPlan.is_popular === true;
                    console.log("Current is_popular:", selectedPlan.is_popular, "Setting to:", !current);
                    setSelectedPlan({
                      ...selectedPlan,
                      is_popular: !current,
                    });
                  }}
                  className={cn(
                    "w-6 h-6 border-2 border-black rounded flex items-center justify-center",
                    selectedPlan.is_popular === true ? "bg-[#FFD60A]" : "bg-white"
                  )}
                >
                  {selectedPlan.is_popular === true && <Star className="w-4 h-4" />}
                </button>
                <span className="text-sm font-medium">{t("admin.subscriptions.labels.isPopular")}</span>
              </div>
            </div>

            {/* CTA Text */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t("admin.subscriptions.labels.ctaTextZh")}</label>
                <input
                  type="text"
                  value={selectedPlan.cta_text?.["zh-TW"] || ""}
                  onChange={(e) =>
                    setSelectedPlan({
                      ...selectedPlan,
                      cta_text: { ...selectedPlan.cta_text, "zh-TW": e.target.value },
                    })
                  }
                  placeholder={t("admin.subscriptions.labels.ctaTextZhExample", "開始使用")}
                  className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t("admin.subscriptions.labels.ctaTextEn")}</label>
                <input
                  type="text"
                  value={selectedPlan.cta_text?.en || ""}
                  onChange={(e) =>
                    setSelectedPlan({
                      ...selectedPlan,
                      cta_text: { ...selectedPlan.cta_text, en: e.target.value },
                    })
                  }
                  placeholder={t("admin.subscriptions.labels.ctaTextEnExample", "Get Started")}
                  className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex gap-3 justify-end">
            <button
              onClick={() => {
                setActiveTab("plans");
                setSelectedPlan(null);
              }}
              className="px-4 py-2 border-2 border-black rounded-lg bg-white hover:bg-gray-50"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleSavePlan}
              disabled={isSaving}
              className="px-4 py-2 border-2 border-black rounded-lg bg-[#FF6B9D] font-bold shadow-[4px_4px_0_#000] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t("admin.subscriptions.buttons.saveAndSync")
              )}
            </button>
          </div>
        </div>
      )}

      {/* Create Plan Tab */}
      {activeTab === "create" && (
        <div className="border-2 border-black rounded-xl bg-white shadow-[4px_4px_0_#000] p-6">
          <h3 className="text-xl font-black mb-6">
            {t("admin.subscriptions.createNew")}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold mb-2">
                {t("admin.subscriptions.labels.planId")}
              </label>
              <input
                type="text"
                value={newPlan.name}
                onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value.toLowerCase() })}
                placeholder={t("admin.subscriptions.status.lifetimeKey", "lifetime")}
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">{t("admin.subscriptions.labels.sortOrder")}</label>
              <input
                type="number"
                value={newPlan.sort_order ?? ""}
                onChange={(e) => setNewPlan({ ...newPlan, sort_order: e.target.value === "" ? 99 : parseInt(e.target.value) })}
                placeholder="99"
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">{t("admin.subscriptions.labels.displayNameZh")}</label>
              <input
                type="text"
                value={newPlan.display_name["zh-TW"]}
                onChange={(e) =>
                  setNewPlan({
                    ...newPlan,
                    display_name: { ...newPlan.display_name, "zh-TW": e.target.value },
                  })
                }
                placeholder={t("admin.subscriptions.labels.displayNameZhExample", "終身版")}
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">{t("admin.subscriptions.labels.displayNameEn")}</label>
              <input
                type="text"
                value={newPlan.display_name.en}
                onChange={(e) =>
                  setNewPlan({
                    ...newPlan,
                    display_name: { ...newPlan.display_name, en: e.target.value },
                  })
                }
                placeholder={t("admin.subscriptions.labels.displayNameEnExample", "Lifetime")}
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">{t("admin.subscriptions.labels.lifetimePrice")}</label>
              <input
                type="number"
                step="0.01"
                value={newPlan.lifetime_price || ""}
                onChange={(e) =>
                  setNewPlan({
                    ...newPlan,
                    lifetime_price: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                placeholder="299"
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">{t("admin.subscriptions.labels.monthlyPriceCreate")}</label>
              <input
                type="number"
                step="0.01"
                value={newPlan.monthly_price || ""}
                onChange={(e) =>
                  setNewPlan({
                    ...newPlan,
                    monthly_price: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                className="w-full px-3 py-2 border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D]"
              />
            </div>
          </div>

          {/* Feature Toggles for Create */}
          <div className="mt-6 p-4 border-2 border-black rounded-lg bg-white">
            <h4 className="text-sm font-bold mb-3">{t("admin.subscriptions.labels.featureToggles")}</h4>
            
            {/* 預設功能開關 */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">{t("admin.subscriptions.labels.defaultFeatures")}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: "hasMirrorMode", label: t("admin.subscriptions.features.mirrorMode") },
                  { key: "hasCustomFonts", label: t("admin.subscriptions.features.customFonts") },
                  { key: "hasExport", label: t("admin.subscriptions.features.export") },
                  { key: "hasNoWatermark", label: t("admin.subscriptions.features.noWatermark") },
                  { key: "hasVoiceSync", label: t("admin.subscriptions.features.voiceSync") },
                  { key: "hasOfflineMode", label: t("admin.subscriptions.features.offlineMode") },
                  { key: "hasPrioritySupport", label: t("admin.subscriptions.features.prioritySupport") },
                ].map((feature) => (
                  <label key={feature.key} className="flex items-center gap-2 cursor-pointer">
                    <button
                      type="button"
                      onClick={() =>
                        setNewPlan({
                          ...newPlan,
                          limits: {
                            ...newPlan.limits,
                            [feature.key]: !newPlan.limits?.[feature.key as keyof typeof newPlan.limits],
                          },
                        })
                      }
                      className={cn(
                        "w-5 h-5 border-2 border-black rounded flex items-center justify-center",
                        newPlan.limits?.[feature.key as keyof typeof newPlan.limits]
                          ? "bg-[#6FEDD6]"
                          : "bg-white"
                      )}
                    >
                      {newPlan.limits?.[feature.key as keyof typeof newPlan.limits] && (
                        <Check className="w-3 h-3" />
                      )}
                    </button>
                    <span className="text-sm">{feature.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 自定義功能開關 */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500 mb-2">{t("admin.subscriptions.labels.customToggles")}</p>
              <div className="space-y-3">
                {(newPlan.custom_toggles || []).map((toggle, index) => (
                  <div key={index} className="p-3 border-2 border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newToggles = [...(newPlan.custom_toggles || [])];
                          newToggles[index] = { ...toggle, enabled: !toggle.enabled };
                          setNewPlan({ ...newPlan, custom_toggles: newToggles });
                        }}
                        className={cn(
                          "w-6 h-6 border-2 border-black rounded flex items-center justify-center flex-shrink-0",
                          toggle.enabled ? "bg-[#6FEDD6]" : "bg-white"
                        )}
                      >
                        {toggle.enabled && <Check className="w-4 h-4" />}
                      </button>
                      <span className="text-sm font-medium flex-1">
                        {toggle.labels?.["zh-TW"] || toggle.labels?.en || t("admin.subscriptions.labels.unnamedFeature")}
                      </span>
                      <span className="text-xs text-gray-400 font-mono">
                        {toggle.key}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const newToggles = (newPlan.custom_toggles || []).filter((_, i) => i !== index);
                          setNewPlan({ ...newPlan, custom_toggles: newToggles });
                        }}
                        className="px-2 py-1 border-2 border-red-400 text-red-600 rounded hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("admin.subscriptions.labels.chineseName")}</label>
                        <input
                          type="text"
                          value={toggle.labels?.["zh-TW"] || ""}
                          onChange={(e) => {
                            const newToggles = [...(newPlan.custom_toggles || [])];
                            newToggles[index] = { 
                              ...toggle, 
                              labels: { ...toggle.labels, "zh-TW": e.target.value } 
                            };
                            setNewPlan({ ...newPlan, custom_toggles: newToggles });
                          }}
                          placeholder={t("admin.subscriptions.labels.chineseName")}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B9D]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("admin.subscriptions.labels.englishName")}</label>
                        <input
                          type="text"
                          value={toggle.labels?.en || ""}
                          onChange={(e) => {
                            const englishName = e.target.value;
                            const generatedKey = englishName
                              ? 'has' + englishName
                                  .split(' ')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                  .join('')
                              : toggle.key;
                            
                            const newToggles = [...(newPlan.custom_toggles || [])];
                            newToggles[index] = { 
                              ...toggle, 
                              key: generatedKey,
                              labels: { ...toggle.labels, en: englishName } 
                            };
                            setNewPlan({ ...newPlan, custom_toggles: newToggles });
                          }}
                          placeholder={t("admin.subscriptions.labels.englishName")}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#FF6B9D]"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => {
                    const newToggle = {
                      key: `hasCustom${Date.now()}`,
                      enabled: false,
                      labels: { "zh-TW": "", en: "" }
                    };
                    setNewPlan({
                      ...newPlan,
                      custom_toggles: [...(newPlan.custom_toggles || []), newToggle],
                    });
                  }}
                  className="w-full px-3 py-2 border-2 border-dashed border-gray-400 rounded-lg text-gray-500 hover:border-black hover:text-black flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("admin.subscriptions.buttons.addCustomToggle")}
                </button>
              </div>
            </div>
          </div>

          {/* Feature List Editor for Create */}
          <div className="mt-6 p-4 border-2 border-black rounded-lg bg-white">
            <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              {t("admin.subscriptions.labels.featureDescriptions")}
            </h4>
            
            {/* Chinese Features */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">{t("admin.subscriptions.labels.chineseFeatures")}</label>
              <div className="space-y-2">
                {(newPlan.features?.["zh-TW"] || []).map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const newFeatures = [...(newPlan.features?.["zh-TW"] || [])];
                        newFeatures[index] = e.target.value;
                        setNewPlan({
                          ...newPlan,
                          features: { ...newPlan.features, "zh-TW": newFeatures },
                        });
                      }}
                      className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D] focus:border-black"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newFeatures = (newPlan.features?.["zh-TW"] || []).filter((_, i) => i !== index);
                        setNewPlan({
                          ...newPlan,
                          features: { ...newPlan.features, "zh-TW": newFeatures },
                        });
                      }}
                      className="px-3 py-2 border-2 border-red-400 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newFeatures = [...(newPlan.features?.["zh-TW"] || []), ""];
                    setNewPlan({
                      ...newPlan,
                      features: { ...newPlan.features, "zh-TW": newFeatures },
                    });
                  }}
                  className="w-full px-3 py-2 border-2 border-dashed border-gray-400 rounded-lg text-gray-500 hover:border-black hover:text-black flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("admin.subscriptions.buttons.addFeatureZh")}
                </button>
              </div>
            </div>

            {/* English Features */}
            <div>
              <label className="block text-sm font-medium mb-2">{t("admin.subscriptions.labels.englishFeatures")}</label>
              <div className="space-y-2">
                {(newPlan.features?.en || []).map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const newFeatures = [...(newPlan.features?.en || [])];
                        newFeatures[index] = e.target.value;
                        setNewPlan({
                          ...newPlan,
                          features: { ...newPlan.features, en: newFeatures },
                        });
                      }}
                      className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B9D] focus:border-black"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newFeatures = (newPlan.features?.en || []).filter((_, i) => i !== index);
                        setNewPlan({
                          ...newPlan,
                          features: { ...newPlan.features, en: newFeatures },
                        });
                      }}
                      className="px-3 py-2 border-2 border-red-400 text-red-600 rounded-lg hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newFeatures = [...(newPlan.features?.en || []), ""];
                    setNewPlan({
                      ...newPlan,
                      features: { ...newPlan.features, en: newFeatures },
                    });
                  }}
                  className="w-full px-3 py-2 border-2 border-dashed border-gray-400 rounded-lg text-gray-500 hover:border-black hover:text-black flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("admin.subscriptions.buttons.addFeatureEn")}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-8 flex gap-3 justify-end">
            <button
              onClick={() => setActiveTab("plans")}
              className="px-4 py-2 border-2 border-black rounded-lg bg-white hover:bg-gray-50"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleCreatePlan}
              disabled={isSaving}
              className="px-4 py-2 border-2 border-black rounded-lg bg-[#6FEDD6] font-bold shadow-[4px_4px_0_#000] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t("admin.subscriptions.buttons.createAndSync")
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
